import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import { TrashIcon, Cog6ToothIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../shared/EditableField';
import MultiSelectDropdown from '../../shared/MultiSelectDropdown';
import SingleSelectDropdown from '../../shared/SingleSelectDropdown';
import { useSchool } from '@/contexts/SchoolContext';
import PeriodUndefinedBadge from '@/features/discipline/components/PeriodUndefinedBadge';
import { GOVERNED_RULE_TIMEFRAMES } from '@/services/domain/domainRuleValidation';
import { useRuleFormSnapshotSync } from './useRuleFormSnapshotSync';

// Helper function to get singular/plural form for options
const getPluralLabel = (options, value, optionValue) => {
  const option = options.find(opt => opt.value === optionValue);
  if (!option) return optionValue;
  // For config service objects, check for plural/singular properties
  if (value > 1) {
    return option.plural || option.label;
  }
  return option.singular || option.label;
};

const ThresholdRuleForm = ({
  rule = null,
  ruleId = null,
  categoryKey = null,
  onSave,
  onDelete,
  isRecognitionMode = false,
  domainActionDefinitionOptions = null,
  domainLabel = null,
  repairMode = false,
}) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  
  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const defaults = {
      title: domainLabel ? `${domainLabel} threshold rule` : (isRecognitionMode ? 'Milestone Rule' : 'Threshold Rule'),
      groups: [],
      historyFilter: null,
      count: 3,
      countTypeId: domainActionDefinitionOptions?.[0]?.id || null,
      countType: domainActionDefinitionOptions?.[0]?.value || 'mentions',
      timeCount: 1,
      timeframe: 'week',
      consequenceCount: 1,
      consequenceTypeId: domainActionDefinitionOptions?.[0]?.id || null,
      consequenceType: domainActionDefinitionOptions?.[0]?.value || 'demerit',
      active: true
    };
    
    if (!rule) return defaults;
    
    // Handle both flat structure (new) and nested structure (old from Firestore)
    return {
      title: rule.title || rule.name || defaults.title,
      groups: rule.groups || rule.conditions?.groups || defaults.groups,
      historyFilter: rule.historyFilter || rule.conditions?.historyFilter || defaults.historyFilter,
      count: rule.count || rule.conditions?.count || rule.conditions?.mentionCount || defaults.count,
      countType: rule.countType || rule.conditions?.countType || defaults.countType,
      countTypeId: rule.countTypeId || rule.conditions?.countTypeId || null,
      timeCount: rule.timeCount || rule.conditions?.timeCount || defaults.timeCount,
      timeframe: rule.timeframe || rule.conditions?.timeframe || defaults.timeframe,
      consequenceCount: rule.consequenceCount || rule.actions?.consequenceCount || defaults.consequenceCount,
      consequenceType: rule.consequenceType || rule.actions?.consequenceType || rule.actions?.actionType || defaults.consequenceType,
      consequenceTypeId: rule.consequenceTypeId || rule.actions?.consequenceTypeId || null,
      active: rule.active !== undefined ? rule.active : defaults.active,
      ...(rule.id && { id: rule.id })
    };
  });
  const { markLocalChanges, reconcileIncomingData } = useRuleFormSnapshotSync(data, setData);
  
  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [consequenceTypeOptions, setConsequenceTypeOptions] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);
  const [historyOptions, setHistoryOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  useEffect(() => {
    if (repairMode) setShowConfig(true);
  }, [repairMode]);

  const menuRef = useRef();
  const isGovernedDomain = Array.isArray(domainActionDefinitionOptions);

  // Load consequence types from Firestore
  useEffect(() => {
    if (isGovernedDomain) {
      setConsequenceTypeOptions(domainActionDefinitionOptions);
      return undefined;
    }
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypes(schoolId);
        setConsequenceTypeOptions(types.map(c => ({
          value: c.value,
          label: c.label,
          singular: c.singular,
          plural: c.plural,
          id: c.id  // Include the Firestore document ID
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId, domainActionDefinitionOptions, isGovernedDomain]);

  // Load timeframes from Firestore
  useEffect(() => {
    if (isGovernedDomain) {
      setTimeframeOptions(GOVERNED_RULE_TIMEFRAMES);
      return undefined;
    }
    const loadTimeframes = async () => {
      try {
        const options = await disciplineConfig.getTimeframeOptions(schoolId);
        setTimeframeOptions(options);
      } catch (error) {
        console.error('Error loading timeframes:', error);
      }
    };
    loadTimeframes();
  }, [schoolId, isGovernedDomain]);

  // Load student history filters from Firestore
  useEffect(() => {
    const loadHistoryFilters = async () => {
      try {
        const options = await disciplineConfig.getStudentHistoryOptions();
        setHistoryOptions(options);
      } catch (error) {
        console.error('Error loading history filters:', error);
      }
    };
    loadHistoryFilters();
  }, []);

  // Load grades from Firestore
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const options = await disciplineConfig.getGradeSelectOptions(schoolId);
        setGradeOptions(options);
      } catch (error) {
        console.error('Error loading grades:', error);
      }
    };
    loadGrades();
  }, [schoolId]);
  
  // Reconcile every live snapshot, including updates to this same rule id.
  useEffect(() => {
    if (!rule?.id) return;
    reconcileIncomingData({
      title: rule.title || rule.name || (domainLabel ? `${domainLabel} threshold rule` : (isRecognitionMode ? 'Milestone Rule' : 'Threshold Rule')),
      groups: rule.groups || rule.conditions?.groups || [],
      historyFilter: rule.historyFilter || rule.conditions?.historyFilter || null,
      count: rule.count || rule.conditions?.count || rule.conditions?.mentionCount || 3,
      countType: rule.countType || rule.conditions?.countType || domainActionDefinitionOptions?.[0]?.value || 'mentions',
      countTypeId: rule.countTypeId || rule.conditions?.countTypeId || domainActionDefinitionOptions?.[0]?.id || null,
      timeCount: rule.timeCount || rule.conditions?.timeCount || 1,
      timeframe: rule.timeframe || rule.conditions?.timeframe || 'week',
      consequenceCount: rule.consequenceCount || rule.actions?.consequenceCount || 1,
      consequenceType: rule.consequenceType || rule.actions?.consequenceType || rule.actions?.actionType || domainActionDefinitionOptions?.[0]?.value || 'demerit',
      consequenceTypeId: rule.consequenceTypeId || rule.actions?.consequenceTypeId || domainActionDefinitionOptions?.[0]?.id || null,
      active: rule.active !== undefined ? rule.active : true,
      id: rule.id,
    });
  }, [rule, domainActionDefinitionOptions, domainLabel, isRecognitionMode, reconcileIncomingData]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    // If updating countType or consequenceType, also update the corresponding ID field
    if (updates.countType) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === updates.countType);
      if (typeOption) {
        updates.countTypeId = typeOption.id;
      }
    }
    if (updates.consequenceType) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === updates.consequenceType);
      if (typeOption) {
        updates.consequenceTypeId = typeOption.id;
      }
    }

    markLocalChanges(updates);
    const newData = { ...data, ...updates };
    setData(newData);
    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'threshold', actualRuleId);
  }, [data, debouncedSave, categoryKey, ruleId, rule?.id, consequenceTypeOptions, markLocalChanges]);
  
  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };
  
  const getWhoText = () => {
    let text = data.groups.length > 0 ? data.groups.join(', ') : 'any student';
    return text;
  };

  const getHistoryText = () => {
    if (data.historyFilter === 'first-offense') return isRecognitionMode ? 'first recognition' : 'first offense';
    if (data.historyFilter === '2-plus') return isRecognitionMode ? '2+ recognitions' : '2+ offenses';
    if (data.historyFilter === '5-plus') return isRecognitionMode ? '5+ recognitions' : '5+ offenses';
    return null;
  };
  
  // Use centralized configuration (sync methods - these don't query Firestore)
  // gradeOptions now loaded via useEffect above
  // consequenceTypeOptions, timeframeOptions, and historyOptions are now loaded via useEffect above
  // Use same list for count types - all consequence types can be counted
  const countTypeOptions = consequenceTypeOptions;
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>
        
        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3">
            <input
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (domainLabel ? `${domainLabel} threshold rule` : (isRecognitionMode ? 'Milestone Rule' : 'Threshold Rule'));
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="text-base pr-4">
              When <span className="font-medium text-blue-600">{getWhoText()}</span>
              {getHistoryText() && (
                <>
                  {' '}
                  <span
                    className="font-medium text-blue-600 px-1 -mx-0.5 rounded transition-colors cursor-pointer hover:bg-base-200"
                    onClick={() => {
                      // Cycle through history options (excluding null)
                      const validOptions = historyOptions.filter(opt => opt.value !== null);
                      const currentIndex = validOptions.findIndex(opt => opt.value === data.historyFilter);
                      const nextIndex = (currentIndex + 1) % validOptions.length;
                      update({ historyFilter: validOptions[nextIndex].value });
                    }}
                    title="Click to cycle history filter"
                  >
                    ({getHistoryText()})
                  </span>
                </>
              )} {isGovernedDomain ? 'has' : (isRecognitionMode ? 'earns' : 'gets')}{' '}
              <EditableField
                field="count"
                value={data.count}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="countType"
                value={data.countType}
                options={countTypeOptions.map(opt => ({
                  value: opt.value,
                  label: getPluralLabel(countTypeOptions, data.count, opt.value)
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {' '}in{' '}
              <EditableField
                field="timeCount"
                value={data.timeCount}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="timeframe"
                value={data.timeframe}
                options={timeframeOptions.map(opt => ({
                  value: opt.value,
                  label: getPluralLabel(timeframeOptions, data.timeCount, opt.value)
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              <PeriodUndefinedBadge timeframe={data.timeframe} />
              , {isGovernedDomain ? 'create' : (isRecognitionMode ? 'award' : 'issue')}{' '}
              <EditableField
                field="consequenceCount"
                value={data.consequenceCount}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="consequenceType"
                value={data.consequenceType}
                options={consequenceTypeOptions.map(opt => ({
                  value: opt.value,
                  label: data.consequenceCount > 1 ? opt.plural : opt.singular
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
            </div>
            
            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />
              
              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>
                
                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {showConfig && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
              >
                <div className="mt-4 pt-4 border-t border-base-200">
                  <div className="grid grid-cols-2 gap-4">
                    <MultiSelectDropdown
                      label="TARGET GROUPS"
                      value={data.groups}
                      options={gradeOptions}
                      onChange={(newGroups) => update({ groups: newGroups })}
                      placeholder="All grades"
                    />

                    <SingleSelectDropdown
                      label="STUDENT HISTORY"
                      value={data.historyFilter}
                      options={historyOptions}
                      onChange={(newHistory) => update({ historyFilter: newHistory })}
                      allowClear={true}
                    />
                  </div>
                  {data.historyFilter && (
                    <div className="mt-2 text-xs text-base-content/60 italic">
                      {isRecognitionMode
                        ? `"First recognition" means the student has never received a ${consequenceTypeOptions.find(opt => opt.value === data.consequenceType)?.singular?.toLowerCase() || data.consequenceType} before.`
                        : `"First offense" means the student has never received a ${consequenceTypeOptions.find(opt => opt.value === data.consequenceType)?.singular?.toLowerCase() || data.consequenceType} before.`
                      }
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default ThresholdRuleForm;
